import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Grid, Typography, CircularProgress, Box } from '@mui/material';
import { searchProducts } from '../../services/productService'; // Assuming a service function exists or will be created
import StyledProductCard from './StyledProductCard'; // Reusable product card
import Pagination from '../Common/Pagination'; // Reusable pagination
import { trackSearchResults } from '../../utils/marketingAnalytics';
import EmptyState from '../Common/EmptyState';
import { COLORS, FONTS } from '../../theme/tokens';

const SearchResults = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q');
  const page = parseInt(searchParams.get('page') || '1', 10);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const lastTrackedItemList = useRef(null);
  const listAnalytics = useMemo(() => ({
    listId: 'search_results',
    listName: 'search_results'
  }), []);

  useEffect(() => {
    const fetchResults = async () => {
      // Removed the check for !query that returned early

      setLoading(true);
      setError(null);
      try {
        // Assuming searchProducts service function handles API call to /api/products/search
        const data = await searchProducts(query, page);
        setProducts(data.products || []);
        setPagination(data.pagination || null);
        if (!data.products || data.products.length === 0) {
           setError(t('search.noProductsMatching', { query }));
        }
      } catch (err) {
        setError(err.message || t('search.loadFailed'));
        setProducts([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query, page]); // Re-fetch when query or page changes

  useEffect(() => {
    if (loading || products.length === 0) {
      return;
    }

    const trackingKey = `search_results|${query || ''}|${page}`;
    // Last-key comparison (not a set) so revisiting a previous page re-fires.
    if (lastTrackedItemList.current === trackingKey) {
      return;
    }

    // GA4 view_item_list + Meta Search (never ViewCategory on search).
    trackSearchResults(products, query, listAnalytics);
    lastTrackedItemList.current = trackingKey;
  }, [listAnalytics, loading, page, products, query]);

  // Determine the title based on whether a query exists
  const pageTitle = query ? t('search.resultsFor', { query }) : t('search.allProducts');

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 4 } }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontFamily: FONTS.heading, fontWeight: 600, textAlign: { xs: 'left', md: 'center' } }}>
        {pageTitle} {/* Use dynamic title */}
      </Typography>
      {!loading && products.length > 0 && (
        <Typography color="text.secondary" sx={{ mb: 4, textAlign: { xs: 'left', md: 'center' } }}>
          {t('search.resultCount', { count: pagination?.totalItems || products.length })}
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
         <EmptyState
           title={query ? t('search.noResultsFor', { query }) : t('shop.noProductsFound')}
           titleComponent="h2"
           description={error}
           actionLabel={t('search.browseAllProducts')}
           actionTo="/featured"
         />
      )}

      {!loading && !error && products.length > 0 && (
        <>
          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {products.map((product, productIndex) => (
              <Grid item key={product.product_id} xs={12} sm={6} md={4} lg={3}>
                <StyledProductCard product={product} analyticsList={{ ...listAnalytics, index: productIndex }} />
              </Grid>
            ))}
          </Grid>

          {pagination && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                basePath={`/search?q=${encodeURIComponent(query)}`} // Pass query for pagination links
              />
            </Box>
          )}
        </>
      )}

       {!loading && products.length === 0 && !error && (
         <Typography sx={{ mt: 3 }}>
           {t('search.noMatchBefore')}<Link to="/" style={{ color: COLORS.green }}>{t('search.noMatchLink')}</Link>.
         </Typography>
       )}
    </Container>
  );
};

export default SearchResults;
